import { ChangeEvent, FocusEvent } from "react";
import {
    RenewedPermitFormErrorType,
    RenewedPermitFormValuesType, useMarkAllRenewedPermitFormFieldsAsTouched, useModalRenewedPermitData,
    useMoveToRenewedPermitStep, useSetModalRenewedPermitAlertState, useSetModalRenewedPermitFormBlurAndError,
    useSetModalRenewedPermitFormError, useSetModalRenewedPermitValueForm
} from "../stores/renewedPermitModal.store";
import { convertFileToSendToServer } from "../helpers/convertors";
import { object, string, mixed, ValidationError } from 'yup';
import {
    MAX_FILE_SIZE, FILE_TOO_LARGE_PATH, REQUIRED_FIELD_PATH,
    SUPPORTED_FORMATS, FILE_UNSUPPORTED_FORMAT_PATH
} from "../validations/errorMessages";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import OwnershipCarType from "../types/ownershipCar.type";
import useHomePageApi from "../axios/useHomePage.api";

const renewedPermitModalSchema = object().shape({
    citizenComment: string()
        .max(500, 'ההערה יכולה להכיל עד 500 תווים בלבד'),
    scanPersonId: mixed()
        .nonNullable()
        .required(REQUIRED_FIELD_PATH)
        .test(
            "FILE_SIZE",
            FILE_TOO_LARGE_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && value.size <= MAX_FILE_SIZE)
            }
        ).test(
            "FILE_FORMAT",
            FILE_UNSUPPORTED_FORMAT_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && SUPPORTED_FORMATS.includes(value?.contentType))
            }
        ),
    scanCarId: mixed()
        .nonNullable()
        .required(REQUIRED_FIELD_PATH)
        .test(
            "FILE_SIZE",
            FILE_TOO_LARGE_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && value.size <= MAX_FILE_SIZE)
            }
        ).test(
            "FILE_FORMAT",
            FILE_UNSUPPORTED_FORMAT_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && SUPPORTED_FORMATS.includes(value?.contentType))
            }
        ),
});

const useRenewedPermitModalFunctions = () => {


    const { renewedPermitOtpApi } = useHomePageApi();
    const markAllFieldsAsTouched = useMarkAllRenewedPermitFormFieldsAsTouched();
    const setModalRenewedPermitAlertState = useSetModalRenewedPermitAlertState();
    const moveToRenewedPermitStep = useMoveToRenewedPermitStep();
    const setModalRenewedPermitFormError = useSetModalRenewedPermitFormError();
    const setBlur = useSetModalRenewedPermitFormBlurAndError();
    const setValueForm = useSetModalRenewedPermitValueForm();
    const renewedPermitData = useModalRenewedPermitData();
    const { form } = renewedPermitData;

    /**
     * Handles the blur event for OTP input fields.
     * @param event -The focus event from the input field.
     */
    async function handleBlurRenewedPermitInput(event: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>) {

        const field = event.target.name as RenewedPermitFormValuesType;

        const [, errors,] = await validateAllFormFields();

        setBlur(field);
        setModalRenewedPermitFormError(errors);
    }

    async function handleSetRenewedPermitFormFile(event: ChangeEvent<HTMLInputElement>) {

        const file = event.currentTarget.files[0];
        const name = event.currentTarget.name as RenewedPermitFormValuesType;

        const fileDataBase64 = await convertFileToSendToServer(file, event.currentTarget.name);

        setValueForm(name, fileDataBase64);
    }

    function handleSetRenewedPermitFormStr(event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {

        const field = event.target.name as RenewedPermitFormValuesType;
        const value = event.target.value;

        setValueForm(field, value);
    }

    async function submitRenewedPermitForm() {

        setModalRenewedPermitAlertState({ alertStatus: 'loading', message: '', alertColor: '' });
        const { form, isCompany } = renewedPermitData;
        const values = form.values;

        const [isValid, , lastError] = await validateAllFormFields(true);

        if (!isValid) {

            setModalRenewedPermitAlertState({
                alertStatus: 'hasAlert',
                message: `${lastError.field} - ${lastError.message}`,
                alertColor: 'danger'
            });

            return;
        }

        const { personId, vehicleNum, otpCode } = renewedPermitData;

        const response = await renewedPermitOtpApi({
            personId: personId,
            vehicleNum: vehicleNum,
            otpCode: otpCode,
            citizenComment: values.citizenComment,
            isCompany: isCompany === OwnershipCarType.COMPANY,
            scanPersonId: values.scanPersonId,
            scanCarId: values.scanCarId,
            scanWorkStatment: values.scanWorkStatment,
            card: {
                cardAddedNumber: values.cardAddedNumber,
                cardExpireDateMM: values.cardExpireDateMM,
                cardExpireDateYY: values.cardExpireDateYY,
                cardHolderId: values.cardHolderId,
                cardHolderName: values.cardHolderName,
                cardNumber: values.cardNumber,
            }
        });

        if (!response.isSuccess) {

            setModalRenewedPermitAlertState({
                alertStatus: 'hasAlert',
                message: response.message,
                alertColor: 'danger'
            });

            return;
        }

        setModalRenewedPermitAlertState({ alertStatus: 'noAlert', message: '', alertColor: '' });
        moveToRenewedPermitStep(response.displayRenewedPermitConfirmation);
    }

    async function validateAllFormFields(markFieldsAsTouched: boolean = false):
        Promise<[boolean, RenewedPermitFormErrorType, { field: string, message: string }]> {

        const errors: RenewedPermitFormErrorType = {};
        const values = form.values;
        let lastError = { field: '', message: '' };

        let isValid = false;

        try {

            await renewedPermitModalSchema.validate(values, { abortEarly: false });
            isValid = true;
        } catch (err) {

            if (err instanceof ValidationError) {
                err.inner.forEach((error) => {
                    const field = error.path as RenewedPermitFormValuesType;
                    errors[field] = error.message;
                    lastError = { field, message: error.message };
                }
                );
            }
        }

        setModalRenewedPermitFormError(errors);

        if(markFieldsAsTouched)
            markAllFieldsAsTouched();

        console.log('Validation Errors:', errors);
        return [isValid, errors, lastError];
    }

    return {
        submitRenewedPermitForm,
        handleSetRenewedPermitFormFile,
        handleSetRenewedPermitFormStr,
        handleBlurRenewedPermitInput,
    };
}

export default useRenewedPermitModalFunctions;